import type { EmblaCarouselType } from "embla-carousel";
import { type RefObject, useCallback, useEffect, useState } from "react";

type EmblaNavigation = {
	nextDisabled: boolean;
	onNextClick: () => void;
	onPrevClick: () => void;
	prevDisabled: boolean;
	slidesInView: number[];
};

export const useEmblaNavigation = (
	emblaApi: RefObject<EmblaCarouselType | undefined>,
): EmblaNavigation => {
	const [nextDisabled, setNextDisabled] = useState(true);
	const [prevDisabled, setPrevDisabled] = useState(true);
	const [slidesInView, setSlidesInView] = useState<number[]>([]);

	const onPrevClick = useCallback(() => {
		if (!emblaApi?.current) return;
		emblaApi.current.scrollPrev();
	}, [emblaApi]);

	const onNextClick = useCallback(() => {
		if (!emblaApi?.current) return;
		emblaApi.current.scrollNext();
	}, [emblaApi]);

	const onSelect = useCallback((emblaApi: EmblaCarouselType) => {
		setNextDisabled(!emblaApi.canScrollNext());
		setPrevDisabled(!emblaApi.canScrollPrev());
	}, []);

	const updateSlidesInView = useCallback((emblaApi: EmblaCarouselType) => {
		setSlidesInView(emblaApi.slidesInView());
	}, []);

	// biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
	useEffect(() => {
		const emblaApiInstance = emblaApi?.current;

		if (!emblaApiInstance) return;

		emblaApiInstance.on("reInit", updateSlidesInView).on("slidesInView", updateSlidesInView);
		onSelect(emblaApiInstance);
		emblaApiInstance.on("reInit", onSelect).on("select", onSelect);

		return () => {
			emblaApiInstance.off("reInit", onSelect).off("select", onSelect);
			emblaApiInstance.off("reInit", updateSlidesInView).off("slidesInView", updateSlidesInView);
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [onSelect, updateSlidesInView]);

	return {
		nextDisabled,
		onNextClick,
		onPrevClick,
		prevDisabled,
		slidesInView,
	};
};
